import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  PaperPlaneTilt,
  Paperclip,
  Image as ImageIcon,
  VideoCamera,
  X,
  TextT,
  Play,
} from "@phosphor-icons/react";
import GifPicker from "./GifPicker";
import { compressVideoIfNeeded, needsCompression } from "@/lib/videoCompress";
import { capturePosterFromVideo } from "@/lib/videoPoster";

/**
 * ChatComposer — rich composer for the news feed & chantier chat.
 *
 * Features (Karim 2026-02-22):
 *   • Photo / video attachments (up to 8) with **inline thumbnail previews**
 *     rendered from a local ObjectURL — the author can review BEFORE posting
 *     and remove individual items with a red ✕ overlay on each tile.
 *   • Optional **Title** field (bold headline) so the author can label a
 *     news-feed post like a mini-article. The title also drives the push
 *     notification body so recipients see a catchy headline.
 *   • Sends `title` + `text` + `files[]` in a single multipart POST /chat.
 *
 * The parent component (ChatFeed) owns the state — this stays a controlled
 * component so replies/updates keep working.
 */
export default function ChatComposer({
  text, setText,
  title, setTitle,
  files, setFiles,
  sending, placeholder,
  onSend, onSendGif,
  showTitle = true, // false for regular chantier chat (title only on news feed)
}) {
  const [gifOpen, setGifOpen] = useState(false);
  const [titleOpen, setTitleOpen] = useState(false);
  const inputRef = useRef(null);

  // Build a stable ObjectURL per file so React re-renders don't re-create
  // blobs (memory leak). Cleanup on unmount / list change.
  const previews = useMemo(
    () => files.map((f) => ({
      key: `${f.name}-${f.size}-${f.lastModified || 0}`,
      isVideo: (f.type || "").startsWith("video/"),
      url: URL.createObjectURL(f),
      name: f.name,
    })),
    [files],
  );
  useEffect(() => {
    return () => { previews.forEach((p) => URL.revokeObjectURL(p.url)); };
  }, [previews]);

  const canSend = !sending && (text.trim() || files.length > 0 || (showTitle && (title || "").trim()));

  return (
    <div className="border-t border-white/10 bg-black/60 p-2 relative">
      <GifPicker open={gifOpen} onClose={() => setGifOpen(false)} onSend={(url) => { setGifOpen(false); onSendGif(url); }} />

      {/* Optional Title bar — collapsible so the composer stays lean by default. */}
      {showTitle && (titleOpen || (title || "").length > 0) && (
        <div className="flex items-center gap-1.5 mb-2">
          <TextT size={14} className="text-purple-300 shrink-0" weight="bold" />
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 100))}
            placeholder="Titre du post (optionnel)…"
            maxLength={100}
            className="flex-1 bg-white/5 border border-purple-500/25 rounded-full px-3 h-8 text-xs font-semibold outline-none focus:border-purple-500/60 placeholder:font-normal placeholder:text-white/40"
            data-testid="chat-title-input"
          />
          <span className="text-[9px] text-white/40 tabular-nums w-8 text-right">{(title || "").length}/100</span>
        </div>
      )}

      {/* Inline attachment previews — the star of this refactor. */}
      {previews.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5 mb-2" data-testid="chat-attachments-preview">
          {previews.map((p, idx) => (
            <div key={p.key} className="relative aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10 group">
              {p.isVideo ? (
                <>
                  <video src={p.url} className="w-full h-full object-cover" muted playsInline />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Play size={22} weight="fill" className="text-white drop-shadow-lg" />
                  </div>
                </>
              ) : (
                <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
              )}
              <button
                onClick={() => setFiles((s) => s.filter((_, i) => i !== idx))}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-500 flex items-center justify-center text-white transition-colors shadow"
                title="Retirer"
                data-testid={`chat-preview-remove-${idx}`}
              >
                <X size={11} weight="bold" />
              </button>
              <div className="absolute bottom-1 left-1 text-[9px] bg-black/60 rounded px-1 py-0.5 text-white/80 flex items-center gap-1">
                {p.isVideo ? <VideoCamera size={9} /> : <ImageIcon size={9} />}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-1.5">
        <label
          className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center cursor-pointer shrink-0"
          title="Joindre photo/vidéo"
        >
          <Paperclip size={16} className="text-white/70" />
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={async (e) => {
              const list = Array.from(e.target.files || []);
              e.target.value = "";
              // Compress heavy videos client-side before they enter the queue,
              // then capture a JPEG poster so the news-feed card can render a
              // thumbnail instantly instead of a black rectangle.
              const processed = [];
              for (const f of list) {
                let file = f;
                if (needsCompression(f)) {
                  const before = f.size;
                  const tId = toast.loading(`Compression de ${f.name}…`);
                  file = await compressVideoIfNeeded(f);
                  toast.dismiss(tId);
                  if (file !== f && file.size < before) {
                    const pct = Math.round((1 - file.size / before) * 100);
                    toast.success(`Vidéo allégée de ${pct}%`);
                  }
                }
                if ((file.type || "").startsWith("video/")) {
                  const poster = await capturePosterFromVideo(file);
                  if (poster) {
                    // Attach the poster on the File so the parent's send()
                    // can append it in the exact video order (no parallel
                    // state to keep in sync).
                    try { Object.defineProperty(file, "_poster", { value: poster }); } catch { /* frozen file → skip */ }
                  }
                }
                processed.push(file);
              }
              setFiles((s) => [...s, ...processed].slice(0, 8));
            }}
            data-testid="chat-file-input"
          />
        </label>
        <button
          type="button"
          onClick={() => setGifOpen((v) => !v)}
          className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center shrink-0 relative"
          title="Envoyer un GIF"
          data-testid="chat-gif-btn"
        >
          <span className="text-[10px] font-black tracking-tight text-white/80">GIF</span>
        </button>
        {showTitle && (
          <button
            type="button"
            onClick={() => setTitleOpen((v) => !v)}
            className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
              titleOpen || (title || "").length > 0
                ? "bg-purple-500/20 border-purple-500/40 text-purple-300"
                : "bg-white/5 hover:bg-white/10 border-white/10 text-white/70"
            }`}
            title="Ajouter un titre"
            data-testid="chat-title-toggle"
          >
            <TextT size={16} weight="bold" />
          </button>
        )}
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder={placeholder}
          rows={1}
          className="flex-1 resize-none bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm outline-none focus:border-[#005CFF]/50 max-h-32"
          data-testid="chat-input"
        />
        <button
          onClick={onSend}
          disabled={!canSend}
          className="w-10 h-10 rounded-full bg-[#005CFF] hover:bg-[#0047CC] disabled:opacity-40 flex items-center justify-center shrink-0 active:scale-90"
          data-testid="chat-send"
        >
          <PaperPlaneTilt size={16} weight="fill" />
        </button>
      </div>
    </div>
  );
}
